import { StaticPageLayout } from '../layouts/static-page-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { contactService } from '@/services/contact-service';
import { ContactRequest } from '@/types/contact';
import { PageErrorBoundary } from '@/components/page-error-boundary';
import { FormErrorBoundary } from '@/components/form-error-boundary';

export function ContactPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session } = useAuth();
  
  // Check if user is truly authenticated (has both user and valid session)
  const isAuthenticated = user && session;

  // Set page title
  useEffect(() => {
    document.title = 'Contact Us - AceHorizon';
  }, []);

  // Scroll to form if navigated from help page
  useEffect(() => {
    if (location.state?.scrollToForm) {
      const timer = setTimeout(() => {
        const formElement = document.getElementById('contact-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // Small delay to ensure DOM is fully rendered
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Form state
  const [formData, setFormData] = useState<ContactRequest>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({
    type: null,
    message: ''
  });

  // Handle input changes
  const handleInputChange = (field: keyof ContactRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Basic validation
      if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
        setSubmitStatus({
          type: 'error',
          message: 'Please fill in all fields.'
        });
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setSubmitStatus({
          type: 'error',
          message: 'Please enter a valid email address.'
        });
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus({ type: null, message: '' });

      try {
        const response = await contactService.submitContactForm(formData);
        
        if (response.success) {
          setSubmitStatus({
            type: 'success',
            message: 'Thank you for contacting us! We\'ll get back to you soon.'
          });
          
          // Reset form
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
        } else {
          setSubmitStatus({
            type: 'error',
            message: 'Something went wrong. Please try again later.'
          });
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        
        // Check if it's a critical error that should trigger error boundary
        if (error instanceof TypeError || (error as any)?.name === 'ChunkLoadError') {
          // Re-throw critical errors to trigger error boundary
          throw error;
        }
        
        // Handle recoverable errors
        setSubmitStatus({
          type: 'error',
          message: 'Failed to send your message. Please try again later.'
        });
      } finally {
        setIsSubmitting(false);
      }
    } catch (error) {
      // This catch block handles critical errors that should trigger the error boundary
      console.error('Critical error in contact form:', error);
      // Re-throw to trigger error boundary
      throw error;
    }
  };

  // Handle Get Started Free button click
  const handleGetStarted = () => {
    if (isAuthenticated) {
      // User is logged in, navigate to dashboard
      navigate('/dashboard');
    } else {
      // User is not logged in, navigate to signup page
      navigate('/signup');
    }
  };

  // Handle Schedule a Demo button click
  const handleScheduleDemo = () => {
    // Create a mailto link for demo requests
    const subject = 'Demo Request for Ace Horizon';
    const body = 'Hi,\n\nI would like to schedule a demo of Ace Horizon. Please let me know your available times.\n\nBest regards';
    const mailtoLink = `mailto:support@acehorizon.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };
  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "Get detailed help via email with comprehensive responses",
      contact: "support@acehorizon.ai",
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "Speak directly with our support team",
      contact: "+91 9596413917",
      color: "bg-green-500",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20"
    }
  ];

  return (
    <PageErrorBoundary pageName="Contact Us">
      <StaticPageLayout>
        <div className="min-h-screen bg-background">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            
            {/* Hero Section */}
            <div className="text-center py-5 sm:py-12">
              <div className="max-w-4xl mx-auto px-2 sm:px-4">
                <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
                  Contact Us
                </h1>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
                  Have questions or need support? We're here to help! Our dedicated team is ready to assist you with any inquiries about 
                  <span className="text-primary font-semibold"> Ace Horizon</span> and how we can enhance your 
                  <span className="text-primary font-semibold"> learning experience</span>.
                </p>
              </div>
            </div>

            {/* Contact Methods Section */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent text-center">
                How to Reach Us
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                Choose your preferred way to get in touch with our support team
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <Card key={index} className={`border-0 bg-gradient-to-br ${method.gradient} transition-all duration-300 relative hover:shadow-lg hover:scale-105 cursor-pointer group`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
                      <CardHeader className="text-center relative z-10">
                        <div className={`w-16 h-16 ${method.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                          <IconComponent className="h-8 w-8 text-white" />
                        </div>
                        <CardTitle className="text-xl font-bold">{method.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-center relative z-10">
                        <p className="text-muted-foreground mb-3">{method.description}</p>
                        <p className="font-semibold text-primary">{method.contact}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Contact Form & Information Section */}
            <div id="contact-form" className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent text-center">
                Send Us a Message
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Contact Form */}
                <FormErrorBoundary formName="Contact Form">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-2xl font-semibold">Get in Touch</CardTitle>
                      <p className="text-muted-foreground">Fill out the form below and we'll get back to you soon</p>
                    </CardHeader>
                    <CardContent>
                      {/* Status Messages */}
                      {submitStatus.type && (
                        <Alert 
                          className={`mb-4 ${
                            submitStatus.type === 'success' 
                              ? 'bg-green-50 text-green-800 border-green-200' 
                              : 'bg-red-50 text-red-800 border-red-200'
                          }`}
                        >
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                          <AlertDescription>{submitStatus.message}</AlertDescription>
                        </Alert>
                      )}

                      <form className="space-y-4" onSubmit={handleSubmit}>
                        <div>
                          <label className="block text-sm font-medium mb-2">Name</label>
                          <Input 
                            placeholder="Your full name" 
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Email</label>
                          <Input 
                            type="email" 
                            placeholder="your.email@example.com" 
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Subject</label>
                          <Input 
                            placeholder="How can we help you?" 
                            value={formData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Message</label>
                          <Textarea 
                            placeholder="Tell us more about your inquiry..." 
                            rows={5} 
                            value={formData.message}
                            onChange={(e) => handleInputChange('message', e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                        <Button 
                          className="w-full" 
                          size="lg" 
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Sending...' : 'Send Message'}
                          {!isSubmitting && <ArrowRight className="ml-2 h-4 w-4" />}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </FormErrorBoundary>
                
                {/* Contact Information */}
                <div className="space-y-6">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-2xl font-semibold">Contact Information</CardTitle>
                      <p className="text-muted-foreground">Find us through these channels</p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Email</p>
                            <p className="text-muted-foreground">support@acehorizon.ai</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Phone className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Phone</p>
                            <p className="text-muted-foreground">+91 9596413917</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-6 w-6 text-primary" />
                        Office Hours
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-muted-foreground">
                        <p><span className="font-medium text-foreground">Monday - Friday:</span> 9:00 AM - 6:00 PM</p>
                        <p><span className="font-medium text-foreground">Saturday and Sunday:</span> Closed</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
              <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent">
                Ready to Transform Your Learning?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Don't wait to revolutionize your educational experience. Get started with Ace Horizon today and discover the power of AI-enhanced learning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="px-8" onClick={handleGetStarted}>
                  {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" size="lg" className="px-8" onClick={handleScheduleDemo}>
                  Schedule a Demo
                </Button>
              </div>
            </div>

          </div>
        </div>
      </StaticPageLayout>
    </PageErrorBoundary>
  );
}
